/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * HtmlTooltip
 *
 * @file HtmlTooltip.jsx
 * @author Mohamed yasar arabath M
 */

import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import mixins from '../../assets/styles/mixins.scss';
import { getStatusStyles } from '../../common/statusColorUtils';
import ConditionalWrapper from './ConditionalWrapper';

const HtmlTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: 'transparent',
    maxWidth: 220,
    padding: 0,
    boxShadow: '0px 2px 1px -1px rgba(13, 0, 77, 0.15), 0px 1px 1px 0px rgba(13, 0, 77, 0.10), 0px 1px 3px 0px rgba(13, 0, 77, 0.05)'
  },
  arrow: {
    color: mixins.whiteFont,
    fontSize: 14,
    '&::before': {
      border: '1px solid rgba(13, 0, 77, 0.15)'
    }
  },
  tooltipPlacementBottom: {
    '& $arrow': {
      top: '1px !important'
    }
  }
}))(Tooltip);

const useStyles = makeStyles({
  labelWrapper: {
    background: mixins.whiteFont,
    borderRadius: '4px',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    padding: '2px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  tooltipText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight20,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  tooltipTextNoDot: {
    gap: '5px'
  },
  segmentRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    width: 'fit-content'
  },
  statusDotContainer: {
    width: 8,
    height: 8,
    position: 'relative'
  },
  dotBase: {
    borderRadius: '50%',
    width: 8,
    height: 8,
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    top: 0,
    bottom: 0
  },
  tooltipLabel: {
    fontWeight: mixins.fontWeight400
  },
  tooltipValue: {
    fontWeight: mixins.fontWeight600,
    marginLeft: 4
  }
});

const StatusTemplate = ({ segments, isPercentage, showBullet }) => {
  const classes = useStyles();

  return (
    <div className={classes.labelWrapper}>
      <div className={classes.textWrapper}>
        <div className={`${classes.tooltipText} ${showBullet ? '' : classes.tooltipTextNoDot}`}>
          {segments.map((segment) => {
            const dotColor = segment?.dotColor || getStatusStyles(segment?.type, { showBullet })?.color;
            const value = isPercentage ? `${segment.value}%` : segment.value;

            return (
              <ConditionalWrapper
                key={segment.label}
                condition={showBullet}
                wrapper="div"
                wrapperProps={{ className: classes.segmentRow }}
              >
                <>
                  {showBullet && (
                    <div className={classes.statusDotContainer}>
                      <div
                        className={classes.dotBase}
                        style={{ backgroundColor: dotColor, color: dotColor }}
                      />
                    </div>
                  )}
                  <span style={{ color: segment.color || getStatusStyles(segment?.id)?.color }}>
                    <span className={classes.tooltipLabel}>{segment.label}:</span><span className={classes.tooltipValue}>{value}</span>
                  </span>
                </>
              </ConditionalWrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
};

StatusTemplate.defaultProps = {
  isPercentage: true,
  showBullet: false
};

StatusTemplate.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ).isRequired,
  isPercentage: PropTypes.bool,
  showBullet: PropTypes.bool
};

export default HtmlTooltip;
export { StatusTemplate };
